/**
 * Field definitions for the "ASPETTO DHS Alteration & Return Form" popup.
 *
 * These keys mirror backend/constants/rmaAlterationForm.js one for one — the
 * backend owns the mapping onto the raw AcroForm field names inside the PDF
 * template, this file owns how those same keys are laid out on screen.
 *
 * Every field on this form is optional.
 */

export const ALTERATION_MAX_LENGTH = {
  header: 255,
  measurement: 20,
  comment: 2000,
  label: 100,
};

/* Top block of the printed form. */
export const ALTERATION_HEADER_FIELDS = [
  {
    key: "user_id",
    label: "User ID",
    placeholder: "Enter the wearer's full name",
    md: 6,
  },
  {
    key: "form_date",
    label: "Date",
    placeholder: "MM/DD/YYYY",
    md: 6,
  },
  {
    key: "agency",
    label: "Agency",
    placeholder: "Enter agency (e.g. USPP)",
    md: 6,
  },
  {
    key: "academy_class",
    label: "Academy Class",
    placeholder: "Enter academy class",
    md: 6,
  },
  /* Each has its own full-width line on the printed form, in this order. */
  {
    key: "contact_info",
    label: "Contact Info",
    placeholder: "Enter phone or email",
    md: 12,
  },
  {
    key: "return_address",
    label: "Return Address",
    placeholder: "Enter return address",
    md: 12,
  },
];

/*
 * Measurement rows, grouped the way the printed form groups them. Each panel
 * carries the same seven measurements plus a free-text comments box.
 */
const buildMeasurementGroups = (panel) => [
  {
    group: "Overall Length",
    items: [
      { key: `${panel}_shorten`, label: "Shorten" },
      { key: `${panel}_lengthen`, label: "Lengthen" },
    ],
  },
  {
    group: "Underarms",
    items: [{ key: `${panel}_underarm_trim`, label: "Trim" }],
  },
  {
    group: "Upper Chest",
    items: [{ key: `${panel}_upper_chest_trim`, label: "Trim" }],
  },
  {
    group: "Neckline",
    items: [{ key: `${panel}_neckline_scoop`, label: "Scoop" }],
  },
  {
    group: "Width",
    items: [
      { key: `${panel}_widen`, label: "Widen By" },
      { key: `${panel}_narrow`, label: "Narrow By" },
    ],
  },
];

export const ALTERATION_PANELS = [
  {
    key: "front",
    title: "Front Panel",
    commentsKey: "front_comments",
    groups: buildMeasurementGroups("front"),
  },
  {
    key: "back",
    title: "Back Panel",
    commentsKey: "back_comments",
    groups: buildMeasurementGroups("back"),
  },
];

/* Detachable return label at the bottom of the printed form. */
export const ALTERATION_LABEL_FIELDS = [
  {
    key: "rma_number",
    label: "RMA # (return label)",
    placeholder: "Printed on the detachable return label",
    md: 6,
  },
];

export const ALTERATION_MEASUREMENT_KEYS = ALTERATION_PANELS.flatMap((panel) =>
  panel.groups.flatMap((group) => group.items.map((item) => item.key)),
);

export const ALTERATION_COMMENT_KEYS = ALTERATION_PANELS.map(
  (panel) => panel.commentsKey,
);

export const ALTERATION_FIELD_KEYS = [
  ...ALTERATION_HEADER_FIELDS.map((field) => field.key),
  ...ALTERATION_MEASUREMENT_KEYS,
  ...ALTERATION_COMMENT_KEYS,
  ...ALTERATION_LABEL_FIELDS.map((field) => field.key),
];

/** A blank form — every key present and empty, so inputs stay controlled. */
export const buildEmptyAlterationValues = () =>
  ALTERATION_FIELD_KEYS.reduce((values, key) => {
    values[key] = "";
    return values;
  }, {});

/** Keep only known keys, coerced to strings. */
export const pickAlterationValues = (source = {}) =>
  ALTERATION_FIELD_KEYS.reduce((values, key) => {
    const raw = source[key];
    values[key] = raw === null || raw === undefined ? "" : String(raw);
    return values;
  }, {});

export const hasAnyAlterationValue = (values = {}) =>
  ALTERATION_FIELD_KEYS.some((key) => String(values[key] || "").trim() !== "");

/*
 * The printed form states "ALTERATIONS: (1/2 INCH INCREMENTS ONLY)". Entries are
 * still accepted as typed — this only drives a soft warning under the input.
 */
export const isHalfInchIncrement = (value) => {
  const text = String(value || "").trim();
  if (text === "") {
    return true;
  }

  const fraction = text.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (fraction) {
    const [, whole, numerator, denominator] = fraction;
    return Number(denominator) !== 0
      ? isHalfInchIncrement(
          String(Number(whole) + Number(numerator) / Number(denominator)),
        )
      : false;
  }

  const simpleFraction = text.match(/^(\d+)\/(\d+)$/);
  if (simpleFraction) {
    const [, numerator, denominator] = simpleFraction;
    return Number(denominator) !== 0
      ? isHalfInchIncrement(String(Number(numerator) / Number(denominator)))
      : false;
  }

  const numeric = Number(text);
  if (Number.isNaN(numeric)) {
    return false;
  }

  return Math.abs(numeric * 2 - Math.round(numeric * 2)) < 1e-9;
};

export const ALTERATION_DOCUMENT_TITLE =
  "ASPETTO DHS Alteration & Return Form";
